import { useEffect, useState, type ReactNode } from 'react'
import {
  ActionIcon,
  Alert,
  Button,
  Combobox,
  Group,
  Loader,
  Pagination,
  SegmentedControl,
  Select,
  Table,
  Text,
  TextInput,
  Textarea,
  useCombobox,
} from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import {
  IconAlertCircle,
  IconMail,
  IconMessageCircle,
  IconMessages,
  IconPlus,
  IconSpeakerphone,
  IconTrash,
  IconUsersGroup,
  IconX,
} from '@tabler/icons-react'
import {
  CRM_COHORT_ADD_LIMIT,
  CRM_FIRST_NAME_TOKEN,
  CRM_LEAD_SOURCES,
  CRM_LEAD_STATUSES,
  CRM_SMS_MAX_LENGTH,
  CRM_SMS_OPT_OUT,
  crmSmsMaxLength,
  type CrmCampaignChannel,
  type CrmLeadSource,
  type CrmLeadStatus,
} from '@behuman/core-contract'
import {
  CAMPAIGN_PAGE_SIZE,
  COHORT_PAGE_SIZE,
  useAddToCohort,
  useClearCohort,
  useCohort,
  useCreateCampaign,
  useCrmCampaign,
  useCrmCampaigns,
  useLaunchCampaign,
  useLeadSuggestions,
  useMatchingLeadCount,
  useRemoveFromCohort,
  useUpdateCampaign,
  type Campaign,
  type CampaignFields,
  type Delivery,
  type Lead,
} from './hooks'
import {
  CHANNEL_LABELS,
  DELIVERY_STATUS_COLORS,
  DELIVERY_STATUS_LABELS,
  LEAD_SOURCE_LABELS,
  LEAD_STATUS_LABELS,
  SKIP_REASON_LABELS,
  errorMessage,
} from './labels'
import { CrmModal, CrmSection } from './CrmModal'
import { PopulationCohortPicker } from './PopulationCohortPicker'
import { usePopulationHealthAccess } from '../Population/usePopulationHealthAccess'
import {
  CORAL,
  CrmCard,
  CrmChip,
  CrmIconTile,
  CrmSearchInput,
  LeadAvatar,
  LeadStatusBadge,
  NameButton,
  tableHeaderClass,
  tableRowClass,
} from './CrmUi'

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })

const isSending = (campaign: Campaign) => campaign.counts.pending + campaign.counts.sending > 0

function CampaignStatusBadge({ campaign }: { campaign: Campaign }) {
  if (campaign.status === 'draft') return <CrmChip color="gray">Draft</CrmChip>
  return isSending(campaign) ? (
    <CrmChip color="blue">Sending</CrmChip>
  ) : (
    <CrmChip color="green">Launched</CrmChip>
  )
}

const PREVIEW_NAME = 'Jamie'

/** The message as a lead receives it, the way the middleware composes it. */
const composeMessage = (channel: Delivery['channel'], message: string) => {
  const text = message.split(CRM_FIRST_NAME_TOKEN).join(PREVIEW_NAME)
  return channel === 'sms' ? `${text}\n\n${CRM_SMS_OPT_OUT}` : text
}

const sendsSms = (channel: CrmCampaignChannel) => channel !== 'email'
const sendsEmail = (channel: CrmCampaignChannel) => channel !== 'sms'

/** One channel's preview: its name and a detail (e.g. the length) above it. */
function PreviewPane({
  icon,
  label,
  detail,
  children,
}: {
  icon: ReactNode
  label: string
  detail?: string
  children: ReactNode
}) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-[13px]">
        <span className="inline-flex items-center gap-1.5 font-semibold text-[#4A5160]">
          {icon}
          {label}
        </span>
        {detail && <span className="text-[#6B7280] tabular-nums">{detail}</span>}
      </div>
      {children}
    </div>
  )
}

/**
 * The message as a lead receives it, by channel: the text as a chat bubble, the email with its
 * subject. Side by side where there is room (a container query), stacked otherwise.
 */
export function MessagePreview({
  channel,
  subject,
  message,
}: {
  channel: CrmCampaignChannel
  subject: string | null
  message: string
}) {
  const text = message.trim() || 'Your message appears here.'
  const sms = composeMessage('sms', text)
  return (
    <div className="@container flex flex-col gap-3">
      <p className="text-[13px] text-[#6B7280]">Preview for a lead named {PREVIEW_NAME}</p>
      <div
        className={`grid items-stretch gap-4 ${sendsSms(channel) && sendsEmail(channel) ? '@2xl:grid-cols-2' : ''}`}
      >
        {sendsSms(channel) && (
          <PreviewPane
            icon={<IconMessageCircle size={15} />}
            label="Text message"
            detail={`${sms.length} characters${sms.length > 160 ? ` · sent as ${Math.ceil(sms.length / 153)} texts` : ''}`}
          >
            <div className="flex flex-1 flex-col rounded-xl border border-[#E7E9EC] bg-[#F6F7F9] p-4">
              <div
                aria-label="Text message preview"
                className="max-w-[85%] self-start rounded-[18px] rounded-bl-md bg-white px-4 py-3 text-[15px] leading-normal whitespace-pre-wrap text-[#1F2733] shadow-[0_1px_2px_rgba(16,24,40,.06)]"
              >
                {sms}
              </div>
            </div>
          </PreviewPane>
        )}
        {sendsEmail(channel) && (
          <PreviewPane icon={<IconMail size={15} />} label="Email">
            <div
              aria-label="Email preview"
              className="flex flex-1 flex-col overflow-hidden rounded-xl border border-[#E7E9EC] text-sm text-[#1F2733]"
            >
              <div className="border-b border-[#E7E9EC] bg-[#FAFBFC] px-4 py-2.5">
                <span className="text-[#6B7280]">Subject: </span>
                <b>{subject?.trim() || 'No subject yet'}</b>
              </div>
              <div className="flex-1 px-4 py-3 leading-relaxed whitespace-pre-wrap">
                {composeMessage('email', text)}
              </div>
            </div>
          </PreviewPane>
        )}
      </div>
    </div>
  )
}

/**
 * Name, channel, subject (email) and message — creating a campaign and editing a draft. With a
 * `formId` the submit button lives elsewhere (the campaign dialog's footer).
 */
function CampaignForm({
  initial,
  submitLabel,
  submitting,
  onSubmit,
  formId,
  onDirtyChange,
}: {
  initial?: CampaignFields
  submitLabel?: string
  submitting: boolean
  onSubmit: (fields: CampaignFields) => void
  formId?: string
  onDirtyChange?: (dirty: boolean) => void
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [channel, setChannel] = useState<CrmCampaignChannel>(initial?.channel ?? 'sms')
  const [subject, setSubject] = useState(initial?.subject ?? '')
  const [message, setMessage] = useState(initial?.message ?? '')
  // The middleware refuses the same: a text must fit one SNS publish at its longest.
  const smsTooLong = sendsSms(channel) && crmSmsMaxLength(message.trim()) > CRM_SMS_MAX_LENGTH
  const ready =
    name.trim() && message.trim() && !smsTooLong && (!sendsEmail(channel) || subject.trim())
  const dirty =
    !initial ||
    name !== initial.name ||
    channel !== initial.channel ||
    subject !== (initial.subject ?? '') ||
    message !== initial.message
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange])
  return (
    <form
      id={formId}
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault()
        if (ready)
          onSubmit({ name, channel, subject: sendsEmail(channel) ? subject : null, message })
      }}
    >
      <TextInput
        readOnly={submitting}
        label="Campaign name"
        radius="md"
        placeholder="Flu shot reminder"
        value={name}
        onChange={(event) => setName(event.currentTarget.value)}
        required
      />
      <div className="flex flex-col gap-1">
        <Text size="sm" fw={500}>
          Send by
        </Text>
        <SegmentedControl
          readOnly={submitting}
          className="self-start"
          radius="md"
          value={channel}
          onChange={(value) => setChannel(value as CrmCampaignChannel)}
          data={[
            { value: 'sms', label: CHANNEL_LABELS.sms },
            { value: 'email', label: CHANNEL_LABELS.email },
            { value: 'both', label: CHANNEL_LABELS.both },
          ]}
        />
      </div>
      {sendsEmail(channel) && (
        <TextInput
          readOnly={submitting}
          label="Subject"
          radius="md"
          value={subject}
          onChange={(event) => setSubject(event.currentTarget.value)}
          required
        />
      )}
      <Textarea
        readOnly={submitting}
        label="Message"
        radius="md"
        autosize
        minRows={4}
        maxRows={10}
        value={message}
        onChange={(event) => setMessage(event.currentTarget.value)}
        required
        description={`Write ${CRM_FIRST_NAME_TOKEN} where the lead’s first name should go.`}
        error={
          smsTooLong &&
          `A text can be up to ${CRM_SMS_MAX_LENGTH.toLocaleString('en-US')} characters, opt-out line included`
        }
      />
      <MessagePreview channel={channel} subject={subject} message={message} />
      {!formId && (
        <Group justify="flex-end">
          <Button
            type="submit"
            color={CORAL}
            radius="md"
            fw={700}
            disabled={!ready}
            loading={submitting}
          >
            {submitLabel}
          </Button>
        </Group>
      )}
    </form>
  )
}

const leadName = (lead: Lead) =>
  [lead.firstName, lead.lastName].filter(Boolean).join(' ') || 'No name yet'

/** Finds one lead by name, phone or email; the dropdown suggests matches as the user types. */
function LeadPicker({ onPick, disabled }: { onPick: (lead: Lead) => void; disabled: boolean }) {
  const combobox = useCombobox({ onDropdownClose: () => combobox.resetSelectedOption() })
  const [query, setQuery] = useState('')
  const [search] = useDebouncedValue(query.trim(), 250)
  const suggestions = useLeadSuggestions(search)
  const leads = search.length >= 2 ? (suggestions.data?.data ?? []) : []
  const searching = search.length >= 2 && suggestions.isFetching

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(value) => {
        const lead = leads.find((candidate) => String(candidate.id) === value)
        if (lead) onPick(lead)
        setQuery('')
        combobox.closeDropdown()
      }}
    >
      <Combobox.Target>
        <CrmSearchInput
          label="Find a lead"
          placeholder="Add a lead by name, phone or email"
          {...(searching && { rightSection: <Loader size={14} /> })}
          value={query}
          disabled={disabled}
          onChange={(value) => {
            setQuery(value)
            combobox.openDropdown()
            combobox.updateSelectedOptionIndex()
          }}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
        />
      </Combobox.Target>
      <Combobox.Dropdown hidden={query.trim().length < 2}>
        <Combobox.Options>
          {leads.length > 0 ? (
            leads.map((lead) => (
              <Combobox.Option key={lead.id} value={String(lead.id)}>
                <div className="flex items-center gap-3">
                  <LeadAvatar lead={lead} />
                  <div className="min-w-0 flex-1">
                    <Text size="sm" fw={700} truncate>
                      {leadName(lead)}
                    </Text>
                    <Text size="xs" c="dimmed" truncate>
                      {[lead.phone, lead.email].filter(Boolean).join(' · ')}
                    </Text>
                  </div>
                  <LeadStatusBadge status={lead.status} />
                </div>
              </Combobox.Option>
            ))
          ) : (
            <Combobox.Empty>
              {searching || search !== query.trim() ? 'Searching…' : 'No leads match'}
            </Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

/** Fills a draft's cohort: one lead found by search, or every lead with a status or source. */
function AddLeadsPanel({
  campaign,
  onBrowseCohorts,
}: {
  campaign: Campaign
  /** Opens the Population cohort picker, which the campaign modal owns. */
  onBrowseCohorts: () => void
}) {
  const [status, setStatus] = useState<CrmLeadStatus | null>(null)
  const [source, setSource] = useState<CrmLeadSource | null>(null)
  const filter = { status: status ?? undefined, source: source ?? undefined }
  const grouped = Boolean(status || source)
  const matching = useMatchingLeadCount(filter)
  const add = useAddToCohort()
  // Kept data is the previous filter's count: hold the button until this filter is counted.
  const counting = matching.data === undefined || Boolean(matching.isPlaceholderData)
  const count = counting ? 0 : (matching.data ?? 0)
  // Population's own flag too: its cohorts aren't shown where Population is hidden.
  const populationHealth = usePopulationHealthAccess() === true

  const addLeads = (input: { leadIds?: number[]; filter?: typeof filter }, who?: string) =>
    add.mutate(
      { campaignId: campaign.id, ...input },
      {
        onSuccess: (result) =>
          notifications.show({
            color: result.added ? 'green' : 'gray',
            message: who
              ? result.added
                ? `Added ${who}`
                : `${who} is already in this campaign`
              : `Added ${result.added} ${result.added === 1 ? 'lead' : 'leads'}`,
          }),
        onError: (error) =>
          notifications.show({
            color: 'red',
            message: errorMessage(error, 'Couldn’t add the leads'),
          }),
      },
    )

  return (
    <div className="flex flex-col gap-3">
      <LeadPicker
        disabled={add.isPending}
        onPick={(lead) => addLeads({ leadIds: [lead.id] }, leadName(lead))}
      />
      <div className="flex flex-col gap-2 rounded-xl bg-[#F5F8FC] p-3 text-sm">
        <span className="font-semibold text-[#33383F]">Add everyone who is</span>
        <div className="flex items-center gap-2">
          <Select
            aria-label="Status"
            placeholder="any status"
            size="xs"
            radius="xl"
            clearable
            data={CRM_LEAD_STATUSES.map((value) => ({ value, label: LEAD_STATUS_LABELS[value] }))}
            value={status}
            onChange={(value) => setStatus(value as CrmLeadStatus | null)}
            className="min-w-0 flex-1"
          />
          <span className="text-[#4A5160]">from</span>
          <Select
            aria-label="Original source"
            placeholder="any source"
            size="xs"
            radius="xl"
            clearable
            data={CRM_LEAD_SOURCES.map((value) => ({ value, label: LEAD_SOURCE_LABELS[value] }))}
            value={source}
            onChange={(value) => setSource(value as CrmLeadSource | null)}
            className="min-w-0 flex-1"
          />
        </div>
        <Button
          size="xs"
          radius="md"
          fw={700}
          className="self-end"
          disabled={!count}
          loading={add.isPending}
          onClick={() => addLeads({ filter })}
        >
          {counting
            ? 'Add matching leads'
            : count > CRM_COHORT_ADD_LIMIT
              ? // One add takes the next CRM_COHORT_ADD_LIMIT not in the list yet; add again for more.
                `Add up to ${CRM_COHORT_ADD_LIMIT.toLocaleString('en-US')} of ${count.toLocaleString('en-US')} leads`
              : grouped
                ? `Add ${count} matching ${count === 1 ? 'lead' : 'leads'}`
                : `Add all ${count} ${count === 1 ? 'lead' : 'leads'}`}
        </Button>
      </div>
      {populationHealth && (
        <div className="flex items-center gap-3 rounded-xl bg-[#F5F8FC] p-3 text-sm">
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="font-semibold text-[#33383F]">Add a Population cohort</span>
            <span className="text-[#4A5160]">Everyone with an open care gap or risk</span>
          </div>
          <Button
            size="xs"
            radius="md"
            fw={700}
            variant="default"
            leftSection={<IconUsersGroup size={14} />}
            onClick={onBrowseCohorts}
          >
            Browse Population cohorts
          </Button>
        </div>
      )}
    </div>
  )
}

/** One chip per message, "SMS · Sent", with the skip reason under a skipped one. */
export function DeliveryChips({ deliveries }: { deliveries: Delivery[] }) {
  return (
    <div className="flex flex-col items-end gap-1">
      {deliveries.map((delivery) => (
        <div key={delivery.channel} className="flex flex-col items-end gap-0.5">
          <CrmChip color={DELIVERY_STATUS_COLORS[delivery.status]}>
            {`${CHANNEL_LABELS[delivery.channel]} · ${DELIVERY_STATUS_LABELS[delivery.status]}`}
          </CrmChip>
          {delivery.skipReason && (
            <span className="text-xs text-[#6B7280]">
              {SKIP_REASON_LABELS[delivery.skipReason]}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

/** Empties a draft's cohort after a confirmation. */
function ClearCohortButton({
  campaign,
  onConfirming,
}: {
  campaign: Campaign
  onConfirming: (open: boolean) => void
}) {
  const [confirming, setConfirming] = useState(false)
  const confirm = (open: boolean) => {
    setConfirming(open)
    onConfirming(open)
  }
  // Gone with its confirmation open (a launch or a clear just landed): the campaign may close again.
  useEffect(() => () => onConfirming(false), [onConfirming])
  const clear = useClearCohort()
  const members = campaign.counts.members
  const run = () =>
    clear.mutate(
      { campaignId: campaign.id },
      {
        onSuccess: (result) => {
          confirm(false)
          notifications.show({
            color: 'green',
            message: `Removed ${result.removed} ${result.removed === 1 ? 'lead' : 'leads'}`,
          })
        },
        onError: (error) =>
          notifications.show({
            color: 'red',
            message: errorMessage(error, 'Couldn’t clear the list'),
          }),
      },
    )
  return (
    <>
      <Button
        size="xs"
        radius="md"
        variant="subtle"
        color="red"
        className="self-end"
        leftSection={<IconTrash size={14} />}
        disabled={!members}
        onClick={() => confirm(true)}
      >
        Clear list
      </Button>
      <CrmModal
        opened={confirming}
        onClose={() => confirm(false)}
        title="Clear the list"
        size="md"
        footer={
          <>
            <Button variant="default" radius="md" onClick={() => confirm(false)}>
              Cancel
            </Button>
            <Button color="red" radius="md" fw={700} loading={clear.isPending} onClick={run}>
              Remove all
            </Button>
          </>
        }
      >
        <Text size="sm">
          Remove all {members} {members === 1 ? 'lead' : 'leads'} from “{campaign.name}”? The leads
          stay in the CRM; only this campaign’s list is emptied.
        </Text>
      </CrmModal>
    </>
  )
}

/** The campaign's recipients: their lead status while a draft, their deliveries once launched. */
function CohortList({ campaign }: { campaign: Campaign }) {
  const [page, setPage] = useState(1)
  const cohort = useCohort(campaign.id, page, isSending(campaign))
  const remove = useRemoveFromCohort()
  const draft = campaign.status === 'draft'
  const rows = cohort.data?.data ?? []
  const total = cohort.data?.page.total ?? 0
  // Removing the last recipient on the last page leaves that page empty: step back to the new last.
  const emptiedPage = Boolean(cohort.data) && !rows.length && page > 1
  useEffect(() => {
    if (emptiedPage) setPage(Math.max(1, Math.ceil(total / COHORT_PAGE_SIZE)))
  }, [emptiedPage, total])
  if (cohort.isPending) return <Loader size="sm" aria-label="Loading the cohort" />
  if (cohort.isError && !cohort.data) {
    return (
      <Alert color="red" variant="light" title="Couldn’t load the recipients">
        <Button size="xs" variant="light" color="red" onClick={() => cohort.refetch()}>
          Try again
        </Button>
      </Alert>
    )
  }
  if (!rows.length) {
    return (
      <div className="rounded-xl border border-dashed border-[#E4E7EB] px-4 py-8 text-center text-sm text-[#6B7280]">
        {draft ? 'No one yet. Search for a lead above, or add a whole group.' : 'No recipients.'}
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-2">
      <ul aria-label="Recipients">
        {rows.map(({ lead, deliveries }) => {
          const name = leadName(lead)
          const contact = [
            sendsSms(campaign.channel) ? lead.phone : null,
            sendsEmail(campaign.channel) ? (lead.email ?? 'No email') : null,
          ]
          return (
            <li
              key={lead.id}
              className="flex min-h-[52px] items-center gap-3 border-b border-[#F0F1F3] py-1.5"
            >
              <LeadAvatar lead={lead} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold text-[#1F2733]">{name}</div>
                <div className="truncate text-xs text-[#6B7280]">
                  {contact.filter(Boolean).join(' · ')}
                </div>
              </div>
              {draft || !deliveries.length ? (
                <LeadStatusBadge status={lead.status} />
              ) : (
                <DeliveryChips deliveries={deliveries} />
              )}
              {draft && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={`Remove ${name}`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate({ campaignId: campaign.id, leadIds: [lead.id] })}
                >
                  <IconX size={14} />
                </ActionIcon>
              )}
            </li>
          )
        })}
      </ul>
      {total > COHORT_PAGE_SIZE && (
        <Pagination
          size="sm"
          radius="md"
          value={page}
          onChange={setPage}
          total={Math.ceil(total / COHORT_PAGE_SIZE)}
          className="self-center"
        />
      )}
    </div>
  )
}

function LaunchButton({
  campaign,
  disabled,
  onConfirming,
}: {
  campaign: Campaign
  disabled: boolean
  onConfirming: (open: boolean) => void
}) {
  const [confirming, setConfirming] = useState(false)
  const confirm = (open: boolean) => {
    setConfirming(open)
    onConfirming(open)
  }
  // Gone with its confirmation open (a launch or a clear just landed): the campaign may close again.
  useEffect(() => () => onConfirming(false), [onConfirming])
  const launch = useLaunchCampaign()
  const members = campaign.counts.members
  const run = () =>
    launch.mutate(
      // The versions on screen: the launch refuses if the message or the recipients changed since.
      {
        campaignId: campaign.id,
        updatedAt: campaign.updatedAt,
        cohortUpdatedAt: campaign.cohortUpdatedAt,
      },
      {
        onSuccess: () => {
          confirm(false)
          notifications.show({ color: 'green', message: `“${campaign.name}” is sending` })
        },
        onError: (error) =>
          notifications.show({
            color: 'red',
            message: errorMessage(error, 'Couldn’t launch the campaign'),
          }),
      },
    )
  return (
    <>
      <Button
        color={CORAL}
        radius="md"
        fw={700}
        disabled={!members || disabled}
        onClick={() => confirm(true)}
      >
        Launch to {members} {members === 1 ? 'lead' : 'leads'}
      </Button>
      <CrmModal
        opened={confirming}
        onClose={() => confirm(false)}
        title="Launch campaign"
        size="md"
        footer={
          <>
            <Button variant="default" radius="md" onClick={() => confirm(false)}>
              Cancel
            </Button>
            <Button color={CORAL} radius="md" fw={700} loading={launch.isPending} onClick={run}>
              Send now
            </Button>
          </>
        }
      >
        <Text size="sm">
          Send “{campaign.name}” to {members} {members === 1 ? 'lead' : 'leads'} now,{' '}
          {campaign.channel === 'both'
            ? 'each by text and by email'
            : campaign.channel === 'sms'
              ? 'by text'
              : 'by email'}
          ? The recipients can’t change afterwards. Leads marked Not interested are skipped
          {sendsSms(campaign.channel) ? ', as are texts to leads with no textable phone' : ''}
          {sendsEmail(campaign.channel) ? ' and emails to leads with no email address' : ''}.
        </Text>
      </CrmModal>
    </>
  )
}

function DeliveryTile({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-xl border border-[#E7E9EC] px-4 py-3">
      <div className="text-2xl font-semibold tabular-nums" style={{ color: tone ?? '#1F2733' }}>
        {value}
      </div>
      <div className="text-[13px] font-medium text-[#6B7280]">{label}</div>
    </div>
  )
}

/** How a launched campaign's messages went; failed and still sending only when some are. */
function DeliverySummary({ campaign }: { campaign: Campaign }) {
  const { sent, failed, skipped, pending, sending } = campaign.counts
  const resume = useLaunchCampaign()
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <DeliveryTile label="messages sent" value={sent} tone="#1E6B3A" />
        {failed > 0 && <DeliveryTile label="failed" value={failed} tone="#B42318" />}
        <DeliveryTile label="skipped" value={skipped} />
        {pending + sending > 0 && (
          <DeliveryTile label="still sending" value={pending + sending} tone="#316DAB" />
        )}
      </div>
      {campaign.channel === 'both' && (
        <span className="text-xs text-[#6B7280]">
          Counts are messages: each lead gets a text and an email.
        </span>
      )}
      {pending > 0 && (
        <Button
          size="xs"
          radius="md"
          variant="light"
          className="self-start"
          loading={resume.isPending}
          onClick={() => resume.mutate({ campaignId: campaign.id })}
        >
          Send remaining
        </Button>
      )}
    </div>
  )
}

/** A campaign opened from the list: its message on the left, its recipients on the right. */
function CampaignModal({
  campaignId,
  fallbackName,
  onClose,
}: {
  campaignId: number
  fallbackName: string
  onClose: () => void
}) {
  const query = useCrmCampaign(campaignId)
  const update = useUpdateCampaign()
  const [dirty, setDirty] = useState(false)
  // The cohort picker and the confirmations open over this modal; Escape must close only them,
  // or unsaved edits here would go with it (Mantine closes every open modal on Escape).
  const [browsingCohorts, setBrowsingCohorts] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const campaign = query.data
  const draft = campaign?.status === 'draft'
  const formId = `crm-campaign-${campaignId}`
  // The draft as the editor has it. It follows each newer save while there are no unsaved edits;
  // once edited it holds still, so a refetch can't wipe the edits, and their save is checked
  // against the version they started from.
  const [editing, setEditing] = useState(campaign)
  if (campaign && !dirty && campaign.updatedAt !== editing?.updatedAt) setEditing(campaign)
  return (
    <CrmModal
      opened
      onClose={onClose}
      closeOnEscape={!browsingCohorts && !confirming}
      size={1080}
      title={campaign?.name ?? fallbackName}
      icon={
        <CrmIconTile>
          {campaign?.channel === 'both' ? (
            <IconMessages size={20} />
          ) : campaign?.channel === 'email' ? (
            <IconMail size={20} />
          ) : (
            <IconMessageCircle size={20} />
          )}
        </CrmIconTile>
      }
      badges={campaign && <CampaignStatusBadge campaign={campaign} />}
      subtitle={
        campaign &&
        `${CHANNEL_LABELS[campaign.channel]} · ${
          campaign.launchedAt
            ? `launched by ${campaign.launchedByName} on ${dateFormat.format(new Date(campaign.launchedAt))}`
            : `created by ${campaign.createdByName} on ${dateFormat.format(new Date(campaign.createdAt))}`
        }`
      }
      footer={
        campaign &&
        draft && (
          <>
            {dirty && (
              <span className="mr-auto inline-flex items-center gap-1.5 text-sm font-semibold text-[#A35208]">
                <IconAlertCircle size={16} aria-hidden />
                Save your changes before launching.
              </span>
            )}
            <Button
              type="submit"
              form={formId}
              variant="default"
              radius="md"
              disabled={!dirty}
              loading={update.isPending}
            >
              Save changes
            </Button>
            <LaunchButton campaign={campaign} disabled={dirty} onConfirming={setConfirming} />
          </>
        )
      }
    >
      {query.isError ? (
        <Alert color="red" variant="light" title="Couldn’t load the campaign">
          <Button size="xs" variant="light" color="red" onClick={() => query.refetch()}>
            Try again
          </Button>
        </Alert>
      ) : !campaign ? (
        <Loader size="sm" aria-label="Loading the campaign" />
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <CrmSection title="Message">
            {draft && editing ? (
              <CampaignForm
                key={editing.updatedAt}
                formId={formId}
                initial={editing}
                submitting={update.isPending}
                onDirtyChange={setDirty}
                onSubmit={(fields) =>
                  update.mutate(
                    { campaignId: campaign.id, updatedAt: editing.updatedAt, ...fields },
                    {
                      onSuccess: (saved) => {
                        setEditing(saved)
                        notifications.show({ color: 'green', message: 'Saved' })
                      },
                      onError: (error) =>
                        notifications.show({
                          color: 'red',
                          message: errorMessage(error, 'Couldn’t save the campaign'),
                        }),
                    },
                  )
                }
              />
            ) : (
              <MessagePreview
                channel={campaign.channel}
                subject={campaign.subject}
                message={campaign.message}
              />
            )}
          </CrmSection>

          <CrmSection title="Recipients" count={campaign.counts.members}>
            {draft ? (
              <AddLeadsPanel campaign={campaign} onBrowseCohorts={() => setBrowsingCohorts(true)} />
            ) : (
              <DeliverySummary campaign={campaign} />
            )}
            {draft && <ClearCohortButton campaign={campaign} onConfirming={setConfirming} />}
            <CohortList campaign={campaign} />
          </CrmSection>
        </div>
      )}
      {campaign && draft && browsingCohorts && (
        <PopulationCohortPicker campaign={campaign} onClose={() => setBrowsingCohorts(false)} />
      )}
    </CrmModal>
  )
}

/** Outgoing SMS and email campaigns, each sent to a cohort of leads. */
export function CampaignsTab({ switcher }: { switcher?: ReactNode }) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search.trim(), 300)
  const [creating, setCreating] = useState(false)
  const [openId, setOpenId] = useState<number | null>(null)
  const campaigns = useCrmCampaigns(page, debouncedSearch || undefined)
  const create = useCreateCampaign()
  const rows = campaigns.data?.data ?? []
  const total = campaigns.data?.page.total ?? 0
  const pages = Math.ceil(total / CAMPAIGN_PAGE_SIZE)
  const opened = rows.find((row) => row.id === openId)

  return (
    <CrmCard
      icon={<IconSpeakerphone size={22} className="text-[#E5533D]" stroke={2} />}
      count={campaigns.data ? total : undefined}
      switcher={switcher}
      tools={
        <CrmSearchInput
          label="Search campaigns"
          placeholder="Search campaigns"
          value={search}
          onChange={(value) => {
            setSearch(value)
            setPage(1)
          }}
          className="w-[300px] max-w-full"
        />
      }
      action={
        <Button
          color={CORAL}
          radius="md"
          fw={700}
          leftSection={<IconPlus size={16} stroke={2.5} />}
          onClick={() => setCreating(true)}
        >
          New campaign
        </Button>
      }
    >
      {campaigns.isError ? (
        <Alert color="red" variant="light" title="Couldn’t load the campaigns">
          <Button size="xs" variant="light" color="red" onClick={() => campaigns.refetch()}>
            Try again
          </Button>
        </Alert>
      ) : campaigns.isPending ? (
        <div className="flex justify-center py-12">
          <Loader size="sm" aria-label="Loading campaigns" />
        </div>
      ) : rows.length === 0 ? (
        <p className="py-12 text-center text-sm text-[#6B7280]">
          {debouncedSearch
            ? `No campaigns match “${debouncedSearch}”.`
            : 'No campaigns yet. Create one, add leads to it, and launch it to send.'}
        </p>
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table horizontalSpacing="sm">
            <Table.Thead>
              <Table.Tr className="h-10 border-b border-[#EAECEF]">
                <Table.Th className={tableHeaderClass}>Campaign</Table.Th>
                <Table.Th className={tableHeaderClass}>Status</Table.Th>
                <Table.Th className={tableHeaderClass}>Cohort</Table.Th>
                <Table.Th className={tableHeaderClass}>Messages sent</Table.Th>
                <Table.Th className={tableHeaderClass}>Failed</Table.Th>
                <Table.Th className={tableHeaderClass}>Skipped</Table.Th>
                <Table.Th className={tableHeaderClass}>Created</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((campaign) => (
                <Table.Tr
                  key={campaign.id}
                  onClick={() => setOpenId(campaign.id)}
                  className={tableRowClass}
                  aria-label={`Open ${campaign.name}`}
                >
                  <Table.Td>
                    <NameButton name={campaign.name} onClick={() => setOpenId(campaign.id)} />
                    <div className="text-xs text-[#6B7280]">{CHANNEL_LABELS[campaign.channel]}</div>
                  </Table.Td>
                  <Table.Td>
                    <CampaignStatusBadge campaign={campaign} />
                  </Table.Td>
                  <Table.Td className="tabular-nums">{campaign.counts.members}</Table.Td>
                  <Table.Td className="tabular-nums">
                    {campaign.status === 'draft' ? '—' : campaign.counts.sent}
                  </Table.Td>
                  <Table.Td className="tabular-nums">
                    {campaign.status === 'draft' ? '—' : campaign.counts.failed}
                  </Table.Td>
                  <Table.Td className="tabular-nums">
                    {campaign.status === 'draft' ? '—' : campaign.counts.skipped}
                  </Table.Td>
                  <Table.Td>
                    <div className="whitespace-nowrap">
                      {dateFormat.format(new Date(campaign.createdAt))}
                    </div>
                    <div className="text-xs text-[#6B7280]">{campaign.createdByName}</div>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      {pages > 1 && (
        <div className="flex flex-wrap items-center gap-3 text-sm text-[#6B7280]">
          <span>
            Page {page} of {pages}
          </span>
          <Pagination
            size="sm"
            radius="md"
            value={page}
            onChange={setPage}
            total={pages}
            className="ml-auto"
          />
        </div>
      )}

      <CrmModal
        opened={creating}
        onClose={() => setCreating(false)}
        title="New campaign"
        subtitle="Start a draft. You add leads and launch it from the campaign afterwards."
      >
        <CampaignForm
          submitLabel="Create draft"
          submitting={create.isPending}
          onSubmit={(fields) =>
            create.mutate(fields, {
              onSuccess: (campaign) => {
                setCreating(false)
                setOpenId(campaign.id)
              },
              onError: (error) =>
                notifications.show({
                  color: 'red',
                  message: errorMessage(error, 'Couldn’t create the campaign'),
                }),
            })
          }
        />
      </CrmModal>

      {openId !== null && (
        <CampaignModal
          campaignId={openId}
          fallbackName={opened?.name ?? 'Campaign'}
          onClose={() => setOpenId(null)}
        />
      )}
    </CrmCard>
  )
}
